import * as React from "react";
import { Pause, Play, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { clamp } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { SmartImage } from "@/components/common/SmartImage";

const STORAGE_KEY = "play_gamestore_download";

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export const DownloadDock = () => {
  const [item, setItem] = React.useState(() => readStored());
  const [paused, setPaused] = React.useState(false);

  React.useEffect(() => {
    writeStored(item);
  }, [item]);

  React.useEffect(() => {
    if (!item || paused) return undefined;

    const id = window.setInterval(() => {
      setItem((prev) => {
        if (!prev) return prev;
        const next = clamp((prev.progress ?? 0) + 1.2, 0, 100);
        if (next >= 100) {
          toast.success("Download complete (MOCK)");
          return { ...prev, progress: 100, status: "Completed" };
        }
        return { ...prev, progress: next, status: "Downloading" };
      });
    }, 700);

    return () => window.clearInterval(id);
  }, [item, paused]);

  React.useEffect(() => {
    const handler = (e) => {
      const payload = e?.detail;
      if (!payload) return;
      setItem({
        id: payload.id,
        title: payload.title,
        cover: payload.cover,
        progress: 0,
        status: "Downloading",
      });
      setPaused(false);
    };

    window.addEventListener("play:start-download", handler);
    return () => window.removeEventListener("play:start-download", handler);
  }, []);

  if (!item) return null;

  const done = Number(item.progress) >= 100;

  return (
    <div className="fixed bottom-4 left-4 z-50 w-[320px] max-w-[calc(100vw-32px)]">
      <Card
        className={cn(
          "overflow-hidden rounded-3xl border-border/60 bg-card/50 backdrop-blur",
          "animate-fade-up"
        )}
      >
        <div className="flex items-center gap-3 p-3">
          <div className="relative size-12 overflow-hidden rounded-2xl border border-border/60 bg-muted">
            <SmartImage
              alt={item.title}
              src={item.cover}
              className="h-full w-full"
              imgClassName="object-cover"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{item.title}</div>
            <div className="text-xs text-muted-foreground">
              {done ? "Ready to play" : paused ? "Paused" : "Downloading"}
            </div>

            <div className="mt-2">
              <Progress value={Number(item.progress) || 0} className="h-2" />
              <div className="mt-1 text-right text-[11px] text-muted-foreground">
                {Math.round(Number(item.progress) || 0)}%
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="glass"
              size="icon"
              onClick={() => setPaused((p) => !p)}
              disabled={done}
              aria-label={paused ? "Resume" : "Pause"}
            >
              {paused ? <Play className="size-4" /> : <Pause className="size-4" />}
            </Button>
            <Button
              variant="glass"
              size="icon"
              onClick={() => {
                setItem(null);
                toast.message("Download cleared (MOCK)");
              }}
              aria-label="Close"
            >
              <X className="size-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export function startDownload(game) {
  const event = new CustomEvent("play:start-download", { detail: game });
  window.dispatchEvent(event);
}
